import { Button, Icon, LoadingBlock } from '@opengov/component-library';
import {
  compareColumnSortKeys,
  findStaleColumnGroupIds,
  getSavedColumnSortKey,
  HiddenColumnGroupIndicator,
  IColumnDataSource,
  IColumnSource,
  IExpandedGroupKeys,
  IExpandedRows,
  isColumnDataSource,
  isMultiHierarchyColumnDataSource,
  IStringConstantExpression,
  MultiHierarchyColumnDataSource,
  resolveColumnSortKeys
} from '@opengov/reporting-types';
import type {
  ColumnState,
  GridApi,
  ICellRendererParams,
  ILoadingCellRendererParams,
  INoRowsOverlayParams,
  IRowNode,
  RowNode
} from 'ag-grid-community';
import { get, isEqual, set, unset } from 'lodash';
import React, { useMemo } from 'react';
import styles from './FinancialReport.module.scss';
import { AG_GRID_GROUP_COLUMN_ID, AG_GRID_NEW_COLUMN_PLACEHOLDER_ID } from './agGridConsts';

export function getRowExpandedState(
  node: RowNode | IRowNode,
  expandedRows: IExpandedRows
): { expand: boolean; collapse: boolean } {
  const nodeIsOpen = !!get(expandedRows, createNodePath(node));

  return {
    expand: nodeIsOpen && !node.expanded,
    collapse: !nodeIsOpen && (node.expanded ?? false)
  };
}

export function setExpandedGroupKeysState(node: RowNode | IRowNode, expandedGroupKeys: IExpandedGroupKeys): void {
  if (!node.id) return;
  const expand = node.id in expandedGroupKeys && !node.expanded;
  const collapse = !(node.id in expandedGroupKeys) && (node.expanded ?? false);
  if (expand) {
    node.setExpanded(true);
  } else if (collapse) {
    node.setExpanded(false);
  }
}

export function setRowExpandedState(node: RowNode | IRowNode, expandedRows: IExpandedRows): void {
  const { expand, collapse } = getRowExpandedState(node, expandedRows);
  if (expand) {
    node.setExpanded(true);
  } else if (collapse) {
    node.setExpanded(false);
  }
}

export function addExpandedState(expandedRows: IExpandedRows, path: string[]): IExpandedRows {
  return set(expandedRows, path, {});
}

export function removeExpandedState(expandedRows: IExpandedRows, path: string[]): IExpandedRows {
  unset(expandedRows, path);
  return expandedRows;
}

function getExpandedRowsDepth(expandedRows: IExpandedRows): number {
  return Object.values(expandedRows).reduce((depth, child) => Math.max(depth, 1 + getExpandedRowsDepth(child)), 0);
}

function getExpandedRowsInScope(expandedRows: IExpandedRows, scopePath: string[]): IExpandedRows | undefined {
  return scopePath.length > 0 ? (get(expandedRows, scopePath) as IExpandedRows | undefined) : expandedRows;
}

/**
 * Deepest row level stored as expanded strictly below `scopePath` (an empty path scopes to the
 * whole report), or -1 when nothing below it is expanded. Read from the stored state rather than
 * the grid because the server-side row model only holds rows that have been scrolled into view.
 */
export function getDeepestExpandedLevel(expandedRows: IExpandedRows, scopePath: string[]): number {
  const scope = getExpandedRowsInScope(expandedRows, scopePath);
  const depth = scope ? getExpandedRowsDepth(scope) : 0;
  return depth === 0 ? -1 : scopePath.length - 1 + depth;
}

function removeExpandedRowsAtDepth(expandedRows: IExpandedRows, depth: number): void {
  if (depth < 1) return;
  Object.keys(expandedRows).forEach((key) => {
    if (depth === 1) {
      Reflect.deleteProperty(expandedRows, key);
    } else {
      removeExpandedRowsAtDepth(expandedRows[key], depth - 1);
    }
  });
}

/** Removes every stored expansion at row `level` below `scopePath`, loaded into the grid or not. */
export function removeExpandedStateAtLevel(
  expandedRows: IExpandedRows,
  scopePath: string[],
  level: number
): IExpandedRows {
  const scope = getExpandedRowsInScope(expandedRows, scopePath);
  if (scope) {
    removeExpandedRowsAtDepth(scope, level - scopePath.length + 1);
  }
  return expandedRows;
}

export function createNodePath(node: RowNode | IRowNode): string[] {
  let currentNode: RowNode | IRowNode = node;
  const pathArray: string[] = [];

  while (currentNode.level != 0) {
    // nodeKey is set on breakdown rows as a compound key (symbioteGroupId:breakdownGroupId)
    // to distinguish sibling breakdown rows that share the same groupId.
    // For other multi-hierarchy rows, groupId is the real database ID; fall back to node.key for standard rows.
    const key =
      (currentNode.data as { nodeKey?: string; groupId?: string } | undefined)?.nodeKey ??
      (currentNode.data as { groupId?: string } | undefined)?.groupId ??
      currentNode.key;
    pathArray.unshift(key ?? '');
    if (!currentNode.parent) {
      break;
    }
    currentNode = currentNode.parent;
  }
  pathArray.unshift(currentNode.id ?? ''); // id for top level (level = 0) node

  return pathArray;
}

export function UndoButton(props: { enabled: boolean; onClick: any; text: string }) {
  return (
    <div>
      <Button onClick={props.onClick} disabled={!props.enabled} dataTest="undo-button">
        <Icon icon="action-undo" />
        &nbsp;{props.text}
      </Button>
    </div>
  );
}

export function normalizeAgGridKey(key: string): string {
  if (!key) return '';
  return key.slice().replace(/_\d+$/, '');
}

export function flattenColDefIds(defs: { colId?: string; children?: unknown[] }[]): string[] {
  return defs.flatMap((def) => {
    if (def.children?.length) return flattenColDefIds(def.children as { colId?: string; children?: unknown[] }[]);
    return def.colId ? [def.colId] : [];
  });
}

export function reorderGroupInState(currentState: string[], prefix: string, defOrderIds: string[]): string[] {
  const groupColsInDefOrder = defOrderIds.filter((id) => id.startsWith(`${prefix}.`));
  const firstGroupIdx = currentState.findIndex((id) => id.startsWith(`${prefix}.`));
  if (firstGroupIdx === -1 || groupColsInDefOrder.length === 0) return currentState;
  const beforeGroup = currentState.slice(0, firstGroupIdx);
  const afterGroup = currentState.slice(firstGroupIdx).filter((id) => !id.startsWith(`${prefix}.`));
  return [...beforeGroup, ...groupColsInDefOrder, ...afterGroup];
}

export function clearAffectedGroupOrders(
  columnOrder: Record<string, number>,
  currentColIds: string[],
  prevCountByPrefix: Map<string, number>
): Record<string, number> {
  const currentCountByPrefix = new Map<string, number>();
  for (const colId of currentColIds) {
    const prefix = colId.split('.')[0];
    if (prefix === colId) continue;
    currentCountByPrefix.set(prefix, (currentCountByPrefix.get(prefix) ?? 0) + 1);
  }

  const affectedPrefixes = new Set<string>();
  for (const [prefix, currentCount] of currentCountByPrefix) {
    const prevCount = prevCountByPrefix.get(prefix) ?? 0;
    if (currentCount > prevCount) {
      affectedPrefixes.add(prefix);
    }
  }

  if (affectedPrefixes.size === 0) return columnOrder;

  return Object.fromEntries(Object.entries(columnOrder).filter(([key]) => !affectedPrefixes.has(key.split('.')[0])));
}

/**
 * Saved position of an AG Grid column: its own key, else its group's slot — the BUD-8668 "Empty Values"
 * rename case (see `getSavedColumnSortKey`). `buildColumnSortKeys` overrides this for stale groups.
 */
export const getSortKeyForColumnGroups = (
  currentColumnOrder: Record<string, number>,
  column: ColumnState | { colId: string }
): number | undefined => getSavedColumnSortKey(currentColumnOrder, normalizeAgGridKey(column.colId));

// Saved columnOrder keys never carry AG Grid's `_N` duplicate-column suffix.
function normalizeColIds(colIds: string[]): string[] {
  return colIds.map(normalizeAgGridKey);
}

/**
 * Column groups whose saved order no longer describes their live columns — e.g. a date filter moving from
 * calendar year 2026 to fiscal year 2027 keeps Jul–Dec 2026 but replaces Jan–Jun 2026 with Jan–Jun 2027.
 * `applyColumnOrder` lays such a group out in definition order, the same reset `clearAffectedGroupOrders`
 * applies when a group gains columns in-session. See `findStaleColumnGroupIds`.
 */
export function findStaleColumnGroups(currentColumnOrder: Record<string, number>, colIds: string[]): Set<string> {
  return findStaleColumnGroupIds(currentColumnOrder, normalizeColIds(colIds));
}

/**
 * Sort key per AG Grid column (see `resolveColumnSortKeys`), keyed by the raw colId so callers can look
 * up the grid's column state directly.
 * @param definitionOrderIds all live column ids in column-definition order; defaults to `colIds`
 */
export function buildColumnSortKeys(
  currentColumnOrder: Record<string, number>,
  colIds: string[],
  definitionOrderIds: string[] = colIds
): Map<string, number | undefined> {
  const resolved = resolveColumnSortKeys(
    currentColumnOrder,
    normalizeColIds(colIds),
    normalizeColIds(definitionOrderIds)
  );
  return new Map(colIds.map((colId) => [colId, resolved.get(normalizeAgGridKey(colId))]));
}

/**
 * Live AG Grid colIds in column-definition order. AG Grid v34 with `maintainColumnOrder: true` appends new
 * columns to the end of the display state rather than inserting them at their definition-order positions,
 * so the grid's own state cannot stand in for the definition order.
 */
export function getDefinitionOrderColIds(
  api: GridApi,
  columnDefinitions: { colId?: string; children?: unknown[] }[]
): string[] {
  const agGridColIds = (api.getColumns() ?? []).map((col) => col.getColId());
  const defOrderMap = new Map(flattenColDefIds(columnDefinitions).map((id, idx) => [normalizeAgGridKey(id), idx]));
  const definitionPosition = (colId: string) => defOrderMap.get(normalizeAgGridKey(colId)) ?? Infinity;
  return agGridColIds.slice().sort((a, b) => definitionPosition(a) - definitionPosition(b));
}

/**
 * @param definitionOrderIds all live column ids in column-definition order, used to lay out stale column
 * groups (see `buildColumnSortKeys`); defaults to the grid's current column state order
 */
export function applyColumnOrder(
  api: GridApi,
  currentColumnOrder: Record<string, number>,
  definitionOrderIds?: string[]
) {
  //No reason to apply column order when there is no order or api not ready
  if (!api || !currentColumnOrder || Object.keys(currentColumnOrder).length === 0) {
    return;
  }

  // Get current column state (getColumnState() can return null/undefined in v34 during early lifecycle)
  const columnState = api.getColumnState();
  if (!columnState) return;
  const columns = columnState.map((col) => ({ colId: col.colId }));
  const colIds = columns.map((col) => col.colId);
  const sortKeys = buildColumnSortKeys(currentColumnOrder, colIds, definitionOrderIds ?? colIds);

  // Sort by the desired order
  const sortedColumns = columns
    .slice()
    .sort((a, b) => compareColumnSortKeys(sortKeys.get(a.colId), sortKeys.get(b.colId)));

  if (isEqual(columns, sortedColumns)) {
    return;
  }

  // Apply column state to enforce correct order
  api.applyColumnState({
    state: sortedColumns,
    applyOrder: true,
    defaultState: { sort: null } // Prevents any side effects
  });
}

export function resetColumnOrder(api: GridApi) {
  if (!api) return;
  const primaryColumns = api.getColumns();
  if (!primaryColumns) return;
  const state = primaryColumns.map((col) => ({ colId: col.getColId() }));
  api.applyColumnState({
    state,
    applyOrder: true,
    defaultState: { sort: null }
  });
}

export function applyColumnsVisibility(api: GridApi, currentColumnsHidden: Record<string, boolean>) {
  if (!api) return;

  const columns = api.getColumns();
  if (!columns) return;

  const { columnsToHide, columnsToShow } = columns
    .map((col) => normalizeAgGridKey(col.getColId()))
    .filter((colId) => colId !== AG_GRID_NEW_COLUMN_PLACEHOLDER_ID)
    .reduce<{ columnsToShow: string[]; columnsToHide: string[] }>(
      (acc, colId) => {
        if (currentColumnsHidden[colId]) {
          acc.columnsToHide.push(colId);
        } else {
          acc.columnsToShow.push(colId);
        }
        return acc;
      },
      { columnsToShow: [], columnsToHide: [] }
    );

  // setColumnsVisible can silently reorder columns in AG Grid; snapshot before and restore after.
  const rawColumnState = columnsToShow.length > 0 ? api.getColumnState() : null;
  const columnOrderSnapshot = rawColumnState?.map((col) => ({ colId: col.colId })) ?? null;

  if (columnsToHide.length > 0) {
    api.setColumnsVisible(columnsToHide, false);
  }
  if (columnsToShow.length > 0) {
    api.setColumnsVisible(columnsToShow, true);
  }

  if (columnOrderSnapshot) {
    api.applyColumnState({
      state: columnOrderSnapshot,
      applyOrder: true,
      defaultState: { sort: null }
    });
  }
}

/**
 * Value cell of a parent row whose response has not arrived yet (`pendingValues` on the row data).
 * Rendering a real loading block instead of a silent blank tells the user the number is still
 * coming — and carries `data-row-level`, so the account-summary-load gauge
 * (`[data-row-level="0"] [data-test="loading-block"]`) keeps waiting until every parent's values
 * are on screen, not only the first response's.
 */
const loadingBlockCellClassName = (styles as Record<string, string>).loadingBlockCell;

export const PendingValueCell: React.FC<ICellRendererParams> = (props) => (
  <div data-row-level={props.node.level} className={loadingBlockCellClassName}>
    <LoadingBlock />
  </div>
);

export const CustomLoadingCell: React.FC<ILoadingCellRendererParams> = (props) => {
  const firstColumnWidth = props?.api?.getColumn(AG_GRID_GROUP_COLUMN_ID)?.getActualWidth();
  return (
    <div
      style={{ ['--first-column-width' as any]: `${firstColumnWidth}px` }}
      className={styles.customLoadingCell}
      data-row-level={props.node.level}
    >
      <div className={styles.loadingBlockGroupCell}>
        <LoadingBlock />
      </div>
      <div className={styles.loadingBlockCell}>
        <LoadingBlock />
      </div>
    </div>
  );
};

// Workaround since noRowsOverlayComponentParams does not update when the gridOptions updates
// https://github.com/ag-grid/ag-grid/issues/5301
export const NoRowsOverlay = ({ context }: INoRowsOverlayParams) => {
  const { localeText, get } = context;
  const { canEditReport } = get();

  const overlayText = useMemo(() => {
    if (canEditReport) {
      return `${localeText.zeroRows.noData} ${localeText.zeroRows.editor} ${localeText.zeroRows.editorLink}.`;
    } else {
      return `${localeText.zeroRows.noData} ${localeText.zeroRows.viewer}`;
    }
  }, [get, localeText.zeroRows.editor, localeText.zeroRows.viewer]);

  return <div>{overlayText}</div>;
};

export const areAllColumnGroupHeadersHidden = (
  columnGroups: IColumnSource[] | MultiHierarchyColumnDataSource[]
): boolean => {
  return (columnGroups as IColumnSource[])
    .filter(
      (col): col is IColumnDataSource | MultiHierarchyColumnDataSource =>
        isColumnDataSource(col) || isMultiHierarchyColumnDataSource(col)
    )
    .every((columnGroup) => {
      const columnGroupHeader = (columnGroup.grouping[0] as IStringConstantExpression).constant;
      return columnGroupHeader === HiddenColumnGroupIndicator;
    });
};

export const shouldHideColumnGroupHeaders = (
  columnGroups: IColumnSource[] = [],
  isEditing: boolean = false
): boolean => {
  return !isEditing && areAllColumnGroupHeadersHidden(columnGroups);
};

export const shouldShowColumnGroupsHiddenIndicator = (
  columnGroups: IColumnSource[] = [],
  isEditing: boolean = false
): boolean => {
  return isEditing && areAllColumnGroupHeadersHidden(columnGroups);
};
